<template>
  <div id="terms">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage page="terms" />

        <div class="jrny-orient-container mb-16">
          <div class="route-container mb-4 section-heading">
            <h1>
              <AppGuideIcon :color="iconColor" class="terms-icon" /> TERMS &
              CONDITIONS
            </h1>
          </div>

          <v-text-field
            label="Current Version"
            v-model="latestVersion"
            readonly
            variant="outlined"
            class="mb-4"
            style="min-width: 100%"
          />

          <v-btn
            color="rgba(165, 216, 103, 1)"
            class="elevation-0 font-weight-bold mb-14"
            @click="createNewVersion"
          >
            Create New Version
          </v-btn>

          <div
            class="editor-container"
            :style="{ backgroundColor: editorBackgroundColor }"
          >
            <GlobalQuillEditor
              v-if="editorReady"
              :page="`terms-${latestVersion}`"
              :docId="`${latestVersion}`"
              :firestoreCollection="'terms_versions'"
              :initialData="{ content: editorContent }"
              :allowed-roles="['superAdmin']"
              @update-content="updateEditorContent"
            />
            <v-progress-linear
              v-else
              color="#592892"
              indeterminate
            ></v-progress-linear>
          </div>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import firebase from 'firebase/app'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage.vue'
  import GlobalQuillEditor from '@/components/utilities/GlobalQuillEditor.vue'
  import { currentTheme } from '@/plugins/vuetify'
  import AppGuideIcon from '@/assets/icons/AppGuideIcon.vue'

  export default {
    name: 'TermsAdmin',
    components: {
      HowToUseThisPage,
      GlobalQuillEditor,
      AppGuideIcon,
    },
    data() {
      return {
        editorContent: null,
        latestVersion: 1,
      }
    },
    computed: {
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      editorBackgroundColor() {
        return currentTheme.value === 'dark' ? '#272727' : '#ffffff'
      },
      editorReady() {
        return this.editorContent !== null
      },
    },
    methods: {
      async getConfig() {
        try {
          const configDoc = await db.collection('config').doc('terms').get()
          if (configDoc.exists) {
            this.latestVersion = configDoc.data().latestVersion || 1
          }

          const termsDoc = await db
            .collection('terms_versions')
            .doc(`${this.latestVersion}`)
            .get()
          if (termsDoc.exists) {
            this.editorContent = termsDoc.data().content || ''
          }
        } catch (error) {
          console.error('Error fetching terms:', error)
        }
      },
      async createNewVersion() {
        try {
          const newVersion = this.latestVersion + 1
          await db
            .collection('terms_versions')
            .doc(`${newVersion}`)
            .set({
              content: this.editorContent,
              page: `terms-${newVersion}`,
              autoTranslate: true,
              sourceLocale: 'en',
              createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            })
          await db.collection('config').doc('terms').set(
            {
              latestVersion: newVersion,
              lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
            },
            { merge: true }
          )

          this.latestVersion = newVersion
        } catch (error) {
          console.error('Error creating new version:', error)
        }
      },
      updateEditorContent(content) {
        this.editorContent = content
        // Ensure autoTranslate flag is preserved when content is updated
        this.ensureAutoTranslateFlag()
      },
      async ensureAutoTranslateFlag() {
        try {
          const currentDoc = await db
            .collection('terms_versions')
            .doc(`${this.latestVersion}`)
            .get()

          if (currentDoc.exists) {
            const data = currentDoc.data()
            if (!data.autoTranslate) {
              await db
                .collection('terms_versions')
                .doc(`${this.latestVersion}`)
                .update({
                  autoTranslate: true,
                  sourceLocale: 'en',
                })
              console.log(
                `[TermsAdmin] Set autoTranslate: true for version ${this.latestVersion}`
              )
            }
          }
        } catch (error) {
          console.error('Error ensuring autoTranslate flag:', error)
        }
      },
    },
    async created() {
      await this.getConfig()
      // Ensure the current version has autoTranslate flag
      await this.ensureAutoTranslateFlag()
    },
  }
</script>

<style>
  .section-heading h1 {
    display: flex;
    align-items: center;
  }
  .terms-icon {
    margin-right: 10px;
  }
</style>
